import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { CarClass, CarClassMap } from '../../mappings/textMappings';
import { resetFilters } from '../../../views/recepcao/recepcaoReducer';
import { useEsteiras } from '../../../hooks/useEsteiras';
import './FilterCarModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/** Filtros em vigor na lista, para o modal reabrir mostrando o que está valendo (at-65). */
export interface FiltrosAplicados {
  carClass?: CarClass[];
  carStatus?: string[];
  pendingStatus?: string[];
  releaseStatus?: string[];
}

interface FilterModalProps {
  onCancel: () => void;
  onApplyFilters: (filters: {
    carClass?: CarClass[];
    carStatus?: string[];
    pendingStatus?: string[];
    released?: boolean;
    releaseStatus?: string[];
  }) => void;
  availableClasses: CarClass[];
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  /** Exibe a coluna "Liberação Adm" (usada pela view Administração). */
  showReleaseFilter?: boolean;
  /** Reset customizado; sem ele, limpa os filtros do slice da Recepção (comportamento original). */
  onResetFilters?: () => void;
  /**
   * at-65 (BUG-V43): o que a lista está filtrando AGORA. Sem isto o modal abria sempre
   * em branco, então quem reabria para conferir via "nenhum filtro" numa lista filtrada —
   * e um "Aplicar Filtros" logo após reabrir **limpava tudo em silêncio**, porque enviava
   * o estado vazio. Com o modal fechado, o contador de itens é o único indício de que a
   * lista está filtrada (padrão do Contac, `03-code-conventions.md`), o que torna a
   * reabertura fiel um requisito, não um detalhe.
   */
  filtrosAplicados?: FiltrosAplicados;
}

/**
 * ⚠️ Este modal é **montado condicionalmente** (`{aberto && <FilterCarModal ... />}`), e
 * não com prop `isOpen` (at-65). É o que faz o `useState` inicial ser a única fonte do
 * rascunho: o desenho anterior (montado sempre + `useEffect` de reset) deixava o rascunho
 * descartado sobreviver para a abertura seguinte — mesmo defeito medido na at-61.
 */
const FilterCarModal: React.FC<FilterModalProps> = ({
  onCancel,
  onApplyFilters,
  availableClasses,
  sidebarRef,
  showReleaseFilter,
  onResetFilters,
  filtrosAplicados,
}) => {
  const { esteirasAtivas } = useEsteiras();
  const [selectedClasses, setSelectedClasses] = useState<CarClass[]>(filtrosAplicados?.carClass ?? []);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(filtrosAplicados?.carStatus ?? []);
  const [selectedPendingStatuses, setSelectedPendingStatuses] = useState<string[]>(
    filtrosAplicados?.pendingStatus ?? []
  );
  const [selectedReleaseStatus, setSelectedReleaseStatus] = useState<string[]>(
    filtrosAplicados?.releaseStatus ?? []
  );
  const dispatch = useDispatch();

  const modalRef = useRef<HTMLDivElement>(null);

  const handleResetFilters = () => {
    if (onResetFilters) {
      onResetFilters();
    } else {
      dispatch(resetFilters());
    }
    setSelectedClasses([]);
    setSelectedStatuses([]);
    setSelectedPendingStatuses([]);
    setSelectedReleaseStatus([]);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    // Montado só quando aberto, então o listener não precisa mais de guarda por `isOpen`.
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel, sidebarRef]);

  const handleApply = () => {
    onApplyFilters({
      carClass: selectedClasses,
      carStatus: selectedStatuses,
      pendingStatus: selectedPendingStatuses,
      ...(showReleaseFilter ? { releaseStatus: selectedReleaseStatus } : {}),
    });
    onCancel();
  };

  return (
    <div className="filter-car-modal-overlay">
      <div className="filter-car-modal-content" ref={modalRef}>
        
        {/* Modal Header */}
        <div className="filter-car-modal-header">
          <div className="header-info">
            <h2>Filtrar Veículos</h2>
          </div>
          <button className="close-button" onClick={onCancel} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        {/* Modal Body */}
        <div className="filter-car-modal-body">
          <div className="filter-columns-container">
            
            {/* Column 1: Classe */}
            <div className="filter-column">
              <div className="filter-column-title">Classe</div>
              <div className="filter-checkbox-group">
                {availableClasses.map((carClass) => {
                  const checked = selectedClasses.includes(carClass);
                  return (
                    <label key={carClass} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedClasses((prev) =>
                            e.target.checked
                              ? [...prev, carClass]
                              : prev.filter((c) => c !== carClass)
                          );
                        }}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                      </div>
                      <span className="filter-text">{CarClassMap[carClass] || carClass}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Column 2: Status Atual */}
            <div className="filter-column">
              <div className="filter-column-title">Esteira Atual</div>
              <div className="filter-checkbox-group">
                {esteirasAtivas.map((est) => {
                  const checked = selectedStatuses.includes(est.id);
                  return (
                    <label key={est.id} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedStatuses((prev) =>
                            e.target.checked
                              ? [...prev, est.id]
                              : prev.filter((s) => s !== est.id)
                          );
                        }}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                      </div>
                      <span className="filter-text">{est.nome}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Column 3: Status Pendente */}
            <div className="filter-column">
              <div className="filter-column-title">Esteira Pendente</div>
              <div className="filter-checkbox-group">
                {esteirasAtivas.map((est) => {
                  const checked = selectedPendingStatuses.includes(est.id);
                  return (
                    <label key={est.id} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setSelectedPendingStatuses((prev) =>
                            e.target.checked
                              ? [...prev, est.id]
                              : prev.filter((s) => s !== est.id)
                          );
                        }}
                      />
                      <div className="custom-checkbox">
                        {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                      </div>
                      <span className="filter-text">{est.nome}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Column 4: Liberação Adm (opcional) */}
            {showReleaseFilter && (
              <div className="filter-column">
                <div className="filter-column-title">Liberação Adm</div>
                <div className="filter-checkbox-group">
                  {[
                    { label: 'Total', value: 'TOTAL' },
                    { label: 'Parcial', value: 'PARCIAL' },
                    { label: 'Pendente', value: 'PENDENTE' },
                  ].map(({ label, value }) => {
                    const checked = selectedReleaseStatus.includes(value);
                    return (
                      <label key={label} className={`filter-item-label ${checked ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedReleaseStatus((prev) =>
                              e.target.checked
                                ? [...prev, value]
                                : prev.filter((v) => v !== value)
                            );
                          }}
                        />
                        <div className="custom-checkbox">
                          {checked && <span className="filter-item-checkmark">&#x2714;</span>}
                        </div>
                        <span className="filter-text">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

          {/* Modal Actions */}
          <div className="filter-modal-actions">
            <button
              className="clear-button"
              onClick={handleResetFilters}
              type="button"
            >
              Limpar Filtros
            </button>
            <button
              className="apply-button"
              onClick={handleApply}
              type="button"
            >
              Aplicar Filtros
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FilterCarModal;

